import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  CircularProgress,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import BGGResultCard from './BGGResultCard';

const searchCache = new Map();
const MIN_QUERY_LENGTH = 2;
const DEBOUNCE_DELAY = 400;
const SEARCH_RESULT_LIMIT = 12;

const normalizeQuery = (value) => value.trim().toLowerCase();

const BGGSearch = ({ onSelect, disabled = false }) => {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [hasSearched, setHasSearched] = useState(false);
  const requestIdRef = useRef(0);

  useEffect(() => {
    const normalizedQuery = normalizeQuery(query);

    if (normalizedQuery.length < MIN_QUERY_LENGTH) {
      requestIdRef.current += 1;
      setResults([]);
      setIsLoading(false);
      setError('');
      setHasSearched(false);
      return undefined;
    }

    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;

    const timeoutId = window.setTimeout(async () => {
      if (searchCache.has(normalizedQuery)) {
        if (requestId !== requestIdRef.current) return;

        setResults(searchCache.get(normalizedQuery));
        setError('');
        setIsLoading(false);
        setHasSearched(true);
        return;
      }

      setIsLoading(true);
      setError('');

      try {
        const response = await axios.get('/api/bgg/search', {
          params: { query: normalizedQuery, limit: SEARCH_RESULT_LIMIT },
        });

        if (requestId !== requestIdRef.current) return;

        const games = Array.isArray(response.data) ? response.data : [];
        searchCache.set(normalizedQuery, games);
        setResults(games);
        setHasSearched(true);
      } catch (err) {
        if (requestId !== requestIdRef.current) return;

        console.error('[BGG search error]', err);
        setResults([]);
        setError(t('bgg.search.fetchError'));
        setHasSearched(true);
      } finally {
        if (requestId === requestIdRef.current) {
          setIsLoading(false);
        }
      }
    }, DEBOUNCE_DELAY);

    return () => window.clearTimeout(timeoutId);
  }, [query, t]);

  const showEmptyState =
    hasSearched && !isLoading && !error && results.length === 0;

  return (
    <Stack component="section" spacing={2} sx={{ maxWidth: 760 }}>
      <TextField
        id="bgg-search-input"
        label={t('bgg.search.label')}
        type="search"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder={t('bgg.search.placeholder')}
        autoComplete="off"
        fullWidth
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
          endAdornment: isLoading ? (
            <InputAdornment position="end">
              <CircularProgress size={20} aria-label={t('bgg.search.loading')} />
            </InputAdornment>
          ) : null,
        }}
      />

      {error && (
        <Alert severity="error" variant="outlined">
          {error}
        </Alert>
      )}
      {showEmptyState && (
        <Typography color="text.secondary">{t('bgg.search.noResults')}</Typography>
      )}

      <Stack spacing={1.25} aria-live="polite">
        {results.map((game) => (
          <BGGResultCard
            key={game.id}
            game={game}
            onAdd={onSelect}
            isDisabled={disabled}
          />
        ))}
      </Stack>
    </Stack>
  );
};

export default BGGSearch;
